// Shared image upload helper.
// Images are uploaded to object storage and referenced by URL. Large photos are
// still compressed in the browser first so uploads stay fast.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MAX_UPLOAD_MB = 16;
const MAX_FILE_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
// Keep the encoded data URI comfortably under the 16MB BSON limit (leaves room
// for the rest of the document fields).
const SAFE_DATAURI_BYTES = 11 * 1024 * 1024;

const fileToDataUri = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const drawToJpeg = (img, maxDim, quality) => {
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // Fill white so transparent areas don't turn black when encoding to JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
};

const compressToFit = async (file, maxBytes) => {
  const img = await loadImage(file);
  let maxDim = 2400;
  let quality = 0.85;
  let dataUri = drawToJpeg(img, maxDim, quality);
  for (let attempt = 0; attempt < 14 && dataUri.length > maxBytes; attempt++) {
    if (quality > 0.5) {
      quality -= 0.1;
    } else {
      maxDim = Math.round(maxDim * 0.85);
      quality = 0.85;
    }
    dataUri = drawToJpeg(img, maxDim, quality);
  }
  return dataUri;
};

const dataUriToBlob = (dataUri) => {
  const [meta, b64] = dataUri.split(',');
  const mime = (meta.match(/data:(.*?);base64/) || [])[1] || 'image/jpeg';
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

// Validate + (if needed) compress an image and return a base64 data URI.
export async function processImageToDataUri(file) {
  if (!file) throw new Error('No file selected');
  if (!file.type.startsWith('image/')) throw new Error('Please select an image file');
  if (file.size > MAX_FILE_BYTES) throw new Error(`Image must be ${MAX_UPLOAD_MB}MB or smaller`);

  const original = await fileToDataUri(file);
  // Small enough to store as-is (preserves original format, e.g. transparent PNG logos).
  if (original.length <= SAFE_DATAURI_BYTES) return original;
  // Too large for the DB once encoded — compress it down safely.
  return compressToFit(file, SAFE_DATAURI_BYTES);
}

// Same validation/compression but returns a File for multipart uploads.
export async function processImageToFile(file) {
  const dataUri = await processImageToDataUri(file);
  const blob = dataUriToBlob(dataUri);
  const ext = blob.type === 'image/jpeg' ? 'jpg' : (file.name.split('.').pop() || 'png');
  return new File([blob], `upload.${ext}`, { type: blob.type });
}

// Compress + upload an image to object storage. Returns the served image URL.
export async function uploadImage(file) {
  const processed = await processImageToFile(file);
  const form = new FormData();
  form.append('file', processed);
  const token = localStorage.getItem('admin_token');
  const res = await axios.post(`${API}/upload`, form, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data.url;
}

// Resolve a stored image reference into a usable <img src>.
// - data:/http(s)/blob: URLs are returned as-is (legacy / external)
// - relative backend paths (/api/files/...) are prefixed with the backend URL
export function resolveImageUrl(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
    return url;
  }
  return `${BACKEND_URL}${url}`;
}

